import PageContainer from '../components/PageContainer';
import PageHero from '../components/PageHero';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Building2, Users, Calendar, TrendingUp, CheckCircle, Mail } from 'lucide-react';

const ForBusiness = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Building2,
      title: 'Professionelle Event-Planung',
      description: 'Zugang zu hunderten geprüften Dienstleistern für alle Arten von Firmenevents'
    },
    {
      icon: Users,
      title: 'Zentrale Koordination',
      description: 'Ein Ansprechpartner für alle Dienstleister - weniger Aufwand, mehr Effizienz'
    },
    {
      icon: Calendar,
      title: 'Schnelle Abwicklung',
      description: 'Kurze Reaktionszeiten und direkter Kontakt zu verfügbaren Dienstleistern'
    },
    {
      icon: TrendingUp,
      title: 'Skalierbare Lösungen',
      description: 'Von kleinen Teamevents bis zu großen Firmen-Galas - alles aus einer Hand'
    }
  ];

  const eventTypes = [
    'Firmenfeiern & Jubiläen',
    'Team-Building Events',
    'Messen & Ausstellungen',
    'Produktlaunches',
    'Weihnachtsfeiern',
    'Konferenzen & Tagungen',
    'Kundenevents',
    'After-Work-Events'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Hero */}
      <PageHero
        label="Für Unternehmen"
        labelIcon={Building2}
        title="Events für Unternehmen leicht gemacht"
        subtitle="EventConnect unterstützt Unternehmen bei der Planung und Umsetzung professioneller Firmenevents - von der Idee bis zur erfolgreichen Durchführung."
      />

      {/* Benefits */}
      <section className="py-20 px-4 bg-white">
        <div className="container max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">Warum Unternehmen EventConnect nutzen</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="p-8 bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-2xl">
                <div className="w-14 h-14 bg-[#2B2142] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 px-4">
        <div className="container max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Unsere Event-Lösungen für Unternehmen</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {eventTypes.map((type, idx) => (
              <div key={idx} className="p-6 bg-white rounded-xl border-2 border-gray-100 hover:border-[#2B2142] hover:shadow-lg transition-all">
                <CheckCircle className="w-6 h-6 text-[#2B2142] mb-3" />
                <p className="font-semibold text-gray-900">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Matching Service */}
      <section className="py-20 px-4 bg-white">
        <div className="container max-w-5xl">
          <div className="bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-3xl p-12 text-white">
            <div className="text-center mb-8">
              <Mail className="w-16 h-16 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">Unser Matching-Service für Firmenkunden</h2>
              <p className="text-xl opacity-90 mb-8">
                Beschreibe dein Event und wir stellen dir ein passendes Team aus geprüften Dienstleistern zusammen.
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-4">So funktioniert's:</h3>
              <ol className="space-y-3 text-lg">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-bold">1</span>
                  <span>Beschreibe dein Event im Anfrage-Formular</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-bold">2</span>
                  <span>Wir wählen passende Dienstleister aus</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center font-bold">3</span>
                  <span>Du erhältst Vorschläge und Angebote</span>
                </li>
              </ol>
            </div>
            
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/event-konfigurator')}
                className="px-8 py-4 bg-white text-[#2B2142] font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                data-testid="b2b-start-btn"
              >
                Jetzt Anfrage stellen
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="container max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Bereit für dein nächstes Firmenevent?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Kontaktiere uns für eine persönliche Beratung oder nutze unseren Event-Konfigurator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/event-konfigurator')}
              className="px-8 py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl shadow-lg"
            >
              Event-Konfigurator starten
            </button>
            <button
              onClick={() => navigate('/search')}
              className="px-8 py-4 bg-white border-2 border-[#2B2142] text-[#2B2142] hover:bg-[#F4F2F2] font-semibold rounded-xl"
            >
              Dienstleister durchsuchen
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForBusiness;
